import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import { NativeSelect } from '../ui/NativeSelect';
import { ListFormData } from '@/types/lists';
import { useListsStore } from '@/store/listsStore';
import { useGoalsStore } from '@/store/goalsStore';
import { Calendar, Target, Palette, Sparkles, Loader2, Send } from 'lucide-react';
import { aiService } from '@/services/aiService';
import { personaService } from '@/services/personaService';

// Validation schema for lists (templates are created via TemplateForm)
const listSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title must be less than 100 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters').max(500, 'Description must be less than 500 characters'),
  goalId: z.string().min(1, 'Goal selection is required'),
  deadline: z.string().optional(),
  startDate: z.string().optional(),
  color: z.string().optional(),
}).refine((data) => {
  // Start date should be before or equal to deadline
  if (data.startDate && data.deadline) {
    return new Date(data.startDate) <= new Date(data.deadline);
  }
  return true;
}, {
  message: "Start date must be before or equal to deadline",
  path: ["deadline"]
});

type ListFormInputs = z.infer<typeof listSchema>;

interface ListFormProps {
  isOpen: boolean;
  onClose: () => void;
  listId?: string;
  isEditing?: boolean;
  selectedGoalId?: string;
}

// Predefined colors for visual organization
const PRESET_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#F7DC6F', 
  '#BB8FCE', '#58D68D', '#F8C471', '#82E0AA'
];

// Color picker component
const ColorPicker = ({ value, onChange }: { value?: string; onChange: (color: string) => void }) => (
  <div className="flex gap-2 flex-wrap">
    {PRESET_COLORS.map(color => (
      <button
        key={color}
        type="button"
        className={`w-8 h-8 rounded-full border-2 hover:scale-110 transition-transform ${
          value === color ? 'border-gray-600' : 'border-gray-300'
        }`}
        style={{ backgroundColor: color }}
        onClick={() => onChange(color)}
        aria-label={`Select color ${color}`}
      />
    ))}
  </div>
);

export function ListForm({
  isOpen,
  onClose,
  listId,
  isEditing = false,
  selectedGoalId
}: ListFormProps) {
  const { createList, getListById, updateList } = useListsStore();
  const { goals, initializeGoals } = useGoalsStore();

  // AI-related state
  const [aiInput, setAiInput] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [showAiSection, setShowAiSection] = useState(false);
  
  // Get existing list data if editing
  const existingList = listId ? getListById(listId) : null;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
    getValues
  } = useForm<ListFormInputs>({
    resolver: zodResolver(listSchema),
    defaultValues: {
      title: '',
      description: '',
      goalId: selectedGoalId || '',
      deadline: '',
      startDate: '',
      color: '',
    }
  });

  // Watch form values for AI context
  const watchedValues = watch();

  // Initialize goals if needed
  useEffect(() => {
    if (goals.length === 0) {
      initializeGoals();
    }
  }, [goals.length, initializeGoals]);

  // Update form values when list data changes
  useEffect(() => {
    if (existingList && isOpen) {
      setValue('title', existingList.title);
      setValue('description', existingList.description);
      setValue('goalId', existingList.goalId || '');
      setValue('deadline', existingList.deadline ?
        existingList.deadline.toISOString().split('T')[0] : '');
      setValue('startDate', existingList.startDate ?
        existingList.startDate.toISOString().split('T')[0] : '');
      setValue('color', existingList.color || '');
    } else if (!isEditing && isOpen) {
      // Reset to default values for new lists
      reset({
        title: '',
        description: '',
        goalId: selectedGoalId || '',
        deadline: '',
        startDate: '',
        color: '',
      });
    }
  }, [existingList, isOpen, isEditing, selectedGoalId, setValue, reset]);

  // AI handler function
  const handleAskAI = async () => {
    const selectedGoalId = watchedValues.goalId;
    if (!selectedGoalId) {
      setAiError('Please select a goal first');
      return;
    }

    // If no user input, generate based on goal alone
    const userInputText = aiInput.trim() || 'Create an actionable list to help achieve this goal';

    const selectedGoal = goals.find(g => g.id === selectedGoalId);
    if (!selectedGoal) {
      setAiError('Selected goal not found');
      return;
    }

    if (!aiService.isConfigured()) {
      setAiError('AI service not configured. Please add your API key in Settings.');
      return;
    }

    setIsAiLoading(true);
    setAiError(null);
    
    try {
      const profile = personaService.getProfile();
      const { lists } = useListsStore.getState();
      
      // Get current form values to provide as context
      const currentValues = getValues();
      
      const suggestion = await aiService.generateListSuggestion({
        userInput: userInputText,
        linkedGoal: {
          id: selectedGoal.id,
          title: selectedGoal.title,
          description: selectedGoal.description,
          smartCriteria: selectedGoal.smartCriteria
        },
        existingLists: lists.map(l => ({
          title: l.title,
          description: l.description,
          goalId: l.goalId || '',
          status: l.status
        })),
        userProfile: {
          name: profile.name,
          jobRole: profile.jobRole,
          age: profile.age
        },
        currentFormData: {
          title: currentValues.title,
          description: currentValues.description,
          color: currentValues.color
        }
      });
      
      // Populate form fields with AI suggestion
      setValue('title', suggestion.title);
      setValue('description', suggestion.description);
      if (suggestion.color) {
        setValue('color', suggestion.color);
      }
      
      // Clear AI input after successful generation
      setAiInput('');
      setShowAiSection(false);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Failed to generate suggestion');
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleFormSubmit = async (data: ListFormInputs) => {
    try {
      if (isEditing && listId && existingList) {
        // For updates, prepare the update data with existing list structure
        // Note: status is NOT updated here - it's managed automatically by UniversalCompletionService
        const updates = {
          title: data.title,
          description: data.description,
          goalId: data.goalId,
          deadline: data.deadline ? new Date(data.deadline) : undefined,
          startDate: data.startDate ? new Date(data.startDate) : undefined,
          color: data.color || undefined,
          // Keep existing items when editing
          items: existingList.items,
        };

        await updateList(listId, updates);
      } else {
        // For creation, use the ListFormData format
        const formData: ListFormData = {
          ...data,
          status: 'ready', // New lists always start as 'ready' - automatic transitions handle the rest
          priority: 1, // New lists get top priority within their goal
          deadline: data.deadline ? new Date(data.deadline) : undefined,
          startDate: data.startDate ? new Date(data.startDate) : undefined,
          // Don't include items - they will be added on the list-items page
        };

        await createList(formData);
      }

      handleClose();
    } catch (error) {
      console.error('Error saving list:', error);
    }
  };

  const handleClose = () => {
    reset();
    setAiInput('');
    setAiError(null);
    setShowAiSection(false);
    onClose();
  };

  const handleColorChange = (color: string) => {
    setValue('color', color);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      title={isEditing ? 'Edit List' : 'Create New List'}
      description="Create actionable lists to break down your goals into manageable tasks"
      scrollBehavior="inside"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title *</label>
              <Input
                {...register('title')}
                placeholder="What do you want to accomplish?"
                error={errors.title?.message}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Description *</label>
              <textarea
                {...register('description')}
                placeholder="Describe what this list will help you achieve..."
                className="w-full min-h-[80px] px-3 py-2 text-sm border border-input rounded-md bg-background ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
              {errors.description && (
                <p className="text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>
          </div>

          {/* Goal Link - Required */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              <label className="text-sm font-medium">Link to Goal *</label>
            </div>
            <NativeSelect
              {...register('goalId')}
              error={errors.goalId?.message}
            >
              <option value="">Select a goal...</option>
              {goals.map(goal => (
                <option key={goal.id} value={goal.id}>
                  {goal.title}
                </option>
              ))}
            </NativeSelect>
            {errors.goalId && (
              <p className="text-sm text-red-600">{errors.goalId.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Every list must be linked to a goal to maintain focus and prioritization
            </p>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <label className="text-sm font-medium">Start Date</label>
              </div>
              <Input
                {...register('startDate')}
                type="date"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <label className="text-sm font-medium">Deadline</label>
              </div>
              <Input
                {...register('deadline')}
                type="date"
                min={new Date().toISOString().split('T')[0]}
                error={errors.deadline?.message}
              />
            </div>
          </div>

          {/* Color Theme */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              <label className="text-sm font-medium">Color Theme (Optional)</label>
            </div>
            <ColorPicker 
              value={watch('color')} 
              onChange={handleColorChange} 
            />
          </div>

          {/* AI Assistant Section */}
          {!isEditing && aiService.isConfigured() && (
            <div className="space-y-2 border-t pt-4">
              {!showAiSection ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAiSection(true)}
                  className="w-full gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Ask AI for Help
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      Ask AI for List Help
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAiSection(false);
                        setAiInput('');
                        setAiError(null);
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Close
                    </button>
                  </div>
                  
                  <div className="flex gap-2">
                    <textarea
                      value={aiInput}
                      onChange={(e) => setAiInput(e.target.value)}
                      placeholder={watchedValues.goalId 
                        ? "Optional: Describe what kind of list would help you achieve this goal, or leave blank for AI to suggest... (e.g., 'weekly action plan' or 'preparation checklist')"
                        : "Please select a goal first..."}
                      className="flex-1 min-h-[80px] px-3 py-2 text-sm border border-input rounded-md bg-background ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                      disabled={isAiLoading || !watchedValues.goalId}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          e.preventDefault();
                          handleAskAI();
                        }
                      }}
                    />
                  </div>
                  
                  {aiError && (
                    <Alert variant="destructive" className="text-sm">
                      {aiError}
                    </Alert>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      {watchedValues.goalId ? (
                        <>AI will create a list to advance: <strong>{goals.find(g => g.id === watchedValues.goalId)?.title}</strong></>
                      ) : (
                        'Select a goal above to enable AI assistance'
                      )}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAskAI}
                      disabled={isAiLoading || !watchedValues.goalId}
                      className="gap-2"
                    >
                      {isAiLoading ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Send className="h-3 w-3" />
                          Generate List
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

      </form>
      
      {/* Form Actions - Fixed at bottom */}
      <div className="flex justify-end gap-3 pt-4 border-t mt-4 bg-background sticky bottom-0">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          onClick={handleSubmit(handleFormSubmit)}
        >
          {isSubmitting ? 'Saving...' : isEditing ? 'Update List' : 'Create List'}
        </Button>
      </div>
    </Modal>
  );
}